import type {
  RewardResult,
  ScoreContext,
  ScoreController,
  ScoreState,
  ScoringEvent,
  ScoringStrategy,
  VisibleScore,
} from '../../sdk/scoring';

/**
 * Platform-side owner of the score.
 *
 * The maths is the game's (`ScoringStrategy`); what lives here is the part the platform must
 * guarantee no matter which module is loaded:
 *
 *   - the team score is monotonic. Shards are never subtracted (MVP notes §14), and that has to
 *     survive a module whose strategy is wrong, not merely a module whose strategy is right.
 *   - the applied-event set is the resume point for an interrupted session (PRO-03), so a round
 *     scored before the interruption cannot score again afterwards.
 */

export interface MissionScoreController extends ScoreController {
  getState(): ScoreState;
  /**
   * The reward for the run that just ended.
   *
   * `endedEarly` is the platform's own fact — only the runtime knows whether the mission reached
   * its end — and a game's badge formula needs it, because "fourteen rounds scored" and "the
   * mission finished" are different things once a round can be skipped (R1-1).
   */
  getCompletionReward(options?: { endedEarly: boolean }): RewardResult;
  /** Rehydrates a session that was interrupted mid-mission. */
  restore(state: ScoreState): void;
}

export interface ScoreControllerOptions {
  strategy: ScoringStrategy;
  context: ScoreContext;
  /** Called whenever the visible score changes, so the shell and scene can redraw. */
  onChange?: (score: VisibleScore) => void;
}

export function createScoreController(options: ScoreControllerOptions): MissionScoreController {
  let state = options.strategy.initialState(options.context);

  const visible = (): VisibleScore => options.strategy.getVisibleScore(state);

  return {
    award(event: ScoringEvent): void {
      const next = options.strategy.applyEvent(state, event, options.context);

      // The floor, not a correction: a strategy that returns less keeps the previous total.
      state = next.teamScore < state.teamScore ? { ...next, teamScore: state.teamScore } : next;

      options.onChange?.(visible());
    },

    getVisibleScore: visible,

    getState: () => state,

    getCompletionReward: (completion) =>
      options.strategy.getCompletionReward(state, {
        ...options.context,
        missionEndedEarly: completion?.endedEarly ?? false,
      }),

    /**
     * Rehydrates an interrupted session — through the same floor as `award`.
     *
     * The floor is a platform guarantee about what a child can be shown, not a correction applied
     * to one code path. Corrupt bytes with the right *shape* are inside the storage threat model
     * (D-030), and this used to replace the state wholesale, so a stored `teamScore: -5` put
     * "Number shards: -5" on screen (R1-14). Nothing a mission can do produces a negative total,
     * so nothing claiming one is restored as one.
     */
    restore(restored: ScoreState): void {
      const participation: Record<string, number> = {};
      for (const [playerId, count] of Object.entries(restored.participation)) {
        participation[playerId] = Math.max(0, count);
      }

      state = {
        ...restored,
        teamScore: Math.max(0, restored.teamScore),
        roundsScored: Math.max(0, restored.roundsScored),
        participation,
      };
      options.onChange?.(visible());
    },
  };
}
